import { useState, useEffect } from 'react';

interface SpeechBubbleProps {
  message: string;
  visible: boolean;
  position?: 'above' | 'left' | 'right';
  onComplete?: () => void;
}

const SpeechBubble = ({ message, visible, position = 'above', onComplete }: SpeechBubbleProps) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (!visible) {
      setDisplayedText('');
      setIsTyping(false);
      return;
    }

    setIsTyping(true);
    setDisplayedText('');
    let currentIndex = 0;

    const typeInterval = setInterval(() => {
      if (currentIndex < message.length) {
        setDisplayedText(message.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typeInterval);

        // Auto-dismiss after showing full message
        setTimeout(() => {
          onComplete?.();
        }, 3000);
      }
    }, 50);

    return () => clearInterval(typeInterval);
  }, [message, visible, onComplete]);

  if (!visible) return null;

  const positionStyles: Record<string, string> = {
    above: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    left: 'right-full top-0 mr-2',
    right: 'left-full top-0 ml-2',
  };

  const tailStyles: Record<string, string> = {
    above: 'top-full left-1/2 -translate-x-1/2 border-t-white border-l-transparent border-r-transparent border-b-transparent border-t-8 border-l-6 border-r-6 border-b-0',
    left: 'left-full top-3 border-l-white border-t-transparent border-b-transparent border-r-transparent border-l-8 border-t-6 border-b-6 border-r-0',
    right: 'right-full top-3 border-r-white border-t-transparent border-b-transparent border-l-transparent border-r-8 border-t-6 border-b-6 border-l-0',
  };

  return (
    <div
      className={`absolute ${positionStyles[position]} z-50 animate-fade-in`}
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div className="relative bg-white text-gray-800 px-3 py-2 rounded-lg shadow-lg max-w-[180px]">
        <p className="text-xs font-mono font-bold whitespace-pre-wrap leading-tight">
          {displayedText}
          {isTyping && <span className="animate-pulse">|</span>}
        </p>
        {/* Speech bubble tail */}
        <div
          className={`absolute w-0 h-0 ${tailStyles[position]}`}
          style={{ borderStyle: 'solid' }}
        />
      </div>
    </div>
  );
};

// Message pools
export const RANDOM_MESSAGES = [
  "Poke me!",
  "Hey, you scrolling?",
  "Hire this guy!",
  "Check out the patents!",
  "I built this site btw",
  "Coffee break?",
  "Fun fact: I run on JS",
  "You've been here a while...",
  "Easter egg? Keep looking!",
  "Nice to meet you!",
  "Scroll responsibly",
  "Bio-computing is cool",
  "Did you know fungi can compute?",
  "Quantum superposition vibes",
  "I exist in multiple states",
  "Qubits go brrr",
  "Entanglement is spooky",
  "Schrödinger's developer",
  "Collapsing wave functions...",
  "Quantum tunneling through bugs",
];

export const CLICK_MESSAGES = [
  "Hey! That tickles!",
  "Whoa there!",
  "You found me!",
  "Boop!",
  "What's up?",
  "Need something?",
  "Hi friend!",
  "Click me again, I dare you",
  "Ow!",
  "Easy there!",
];

export const SECTION_MESSAGES: Record<string, string[]> = {
  hero: [
    "Welcome to my kingdom!",
    "Scroll down for the good stuff",
    "This is where the magic starts",
  ],
  about: [
    "That's me! Well, my bio...",
    "Cool neural network, right?",
    "I wrote all this myself",
  ],
  projects: [
    "I made all of these!",
    "Check out my GitHub",
    "Each project = a learning journey",
  ],
  patents: [
    "Yeah, that's a real patent!",
    "Bio-computing is the future",
    "Fungi + quantum = 🤯",
    "Mycelium meets qubits!",
    "Nature's neural network",
  ],
  experience: [
    "Quite a journey so far",
    "Experience points gained!",
    "Level up!",
  ],
  contact: [
    "Go ahead, say hi!",
    "I don't bite... much",
    "Let's connect!",
  ],
};

export const getRandomMessage = (pool: string[] = RANDOM_MESSAGES): string => {
  return pool[Math.floor(Math.random() * pool.length)];
};

export const getClickMessage = (): string => {
  return CLICK_MESSAGES[Math.floor(Math.random() * CLICK_MESSAGES.length)];
};

export const getSectionMessage = (section: string): string => {
  const messages = SECTION_MESSAGES[section] || RANDOM_MESSAGES;
  return messages[Math.floor(Math.random() * messages.length)];
};

export default SpeechBubble;
